// @ts-nocheck
import { Loader2 } from "lucide-react";
import { TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { api } from "@/lib/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export function LessonList({
  lessons,
  onPatch,
  pending,
}: {
  lessons: any[];
  onPatch: (id: string, status: string) => void;
  pending?: boolean;
}) {
  if (!lessons.length) {
    return <p className="text-sm text-muted-foreground">Brak wniosków. Uruchom retro albo poczekaj na job w tle.</p>;
  }
  return (
    <div className="space-y-2">
      {lessons.map((les) => (
        <div key={les.id} className="rounded-md border border-border p-3 text-sm">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <Badge variant="outline">{les.kind || "watch"}</Badge>
            <Badge variant={les.status === "approved" ? "success" : "outline"}>{les.status || "pending"}</Badge>
            {les.source ? <span className="text-[10px] text-muted-foreground">{les.source}</span> : null}
          </div>
          <p className="whitespace-pre-wrap">{les.text}</p>
          {les.status === "pending" ? (
            <div className="mt-2 flex gap-2">
              <Button size="sm" disabled={pending} onClick={() => onPatch(les.id, "approved")}>
                Zatwierdź
              </Button>
              <Button size="sm" variant="outline" disabled={pending} onClick={() => onPatch(les.id, "rejected")}>
                Odrzuć
              </Button>
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

export function RetroBody({
  title,
  tip,
  retro,
  lessons,
  running,
  onRun,
  onPatch,
  patchPending,
  emptyHint,
  runLabel,
}: {
  title: string;
  tip: string;
  retro: any;
  lessons: any[];
  running?: boolean;
  onRun: () => void;
  onPatch: (id: string, status: string) => void;
  patchPending?: boolean;
  emptyHint: string;
  runLabel?: string;
}) {
  const roles = retro?.transcript || {};
  const pendingLessons = lessons.filter((l) => (l.status || "") === "pending").length;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-medium text-slate-900">{title}</h2>
          <p className="text-xs text-muted-foreground">{tip}</p>
        </div>
        <Button type="button" variant="outline" disabled={running} onClick={onRun}>
          {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {runLabel || (retro ? "Uruchom ponownie" : "Uruchom")}
        </Button>
      </div>
      {!retro ? <p className="text-sm text-muted-foreground">{emptyHint}</p> : null}
      {retro ? (
        <Card className="border-slate-200 bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center gap-2 text-base">
              Werdykt
              <Badge variant="outline">{retro.verdict || "brak"}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {retro.decision ? <p>{retro.decision}</p> : null}
            <div className="grid gap-2">
              {Object.entries(roles).map(([role, text]) => (
                <div key={role} className="rounded bg-muted/40 p-2 text-xs">
                  <div className="mb-1 font-medium">{role}</div>
                  <p className="whitespace-pre-wrap text-muted-foreground">{String(text || "")}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : null}
      <Card className="border-slate-200 bg-white shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">
            Wnioski do zatwierdzenia
            {pendingLessons > 0 ? (
              <span className="ml-2 text-sm font-normal text-muted-foreground">({pendingLessons} pending)</span>
            ) : null}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <LessonList lessons={lessons} onPatch={onPatch} pending={patchPending} />
        </CardContent>
      </Card>
    </div>
  );
}

export default function RetroTab({ ctx }: { ctx: any }) {
  const { item, selectedId, retroM, clickAi, retroRunning } = ctx;
  const qc = useQueryClient();
  const patchM = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => api.carouselLessonPatch(id, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carousel", selectedId] });
    },
  });
  const published = item?.status === "published";
  const retro = item?.retro_discussion || (item?.retro?.phase === "discussion" ? item.retro : null);
  const retroId = String(retro?.id || "");
  const lessons = (item?.lessons || []).filter((l: any) => {
    if (retroId) return String(l.retro_id || "") === retroId;
    const src = l.source || "";
    return src === "discussion" || !src;
  });
  const pendingCount = lessons.filter((l: any) => (l.status || "") === "pending").length;
  return (
    <TabsContent value="retro" className="mt-0 space-y-5">
      {!published ? (
        <p className="text-sm text-muted-foreground">
          Najpierw opublikuj. Po publikacji retro dyskusji odpali się w tle, a wnioski pojawią się tu do zatwierdzenia.
        </p>
      ) : (
        <RetroBody
          title="Retro dyskusji"
          tip={
            pendingCount > 0
              ? `${pendingCount} wniosków czeka na zatwierdzenie. Zatwierdzone idą do globalnej pamięci.`
              : "Źródło: czat copy, rewizja, recenzja ról. Po publikacji i republish auto-retro dokłada wnioski tu."
          }
          retro={retro}
          lessons={lessons}
          running={retroRunning || item?.llm_status === "retro"}
          onRun={() => clickAi("retro", () => retroM.mutate({ phase: "discussion" }))}
          onPatch={(id, status) => patchM.mutate({ id, status })}
          patchPending={patchM.isPending}
          emptyHint="Czekam na auto-retro po publikacji albo kliknij Uruchom."
        />
      )}
    </TabsContent>
  );
}
